import React from "react";
import { Box, Container, Typography } from "@mui/material";
import { useTheme } from "@emotion/react";
import MaestroNavbar from "./blocks/MaestroNavbar.js";
import MaestroFooter from "./blocks/MaestroFooter.js";
import ClosingCTA from "./blocks/ClosingCTA.js";
import Seo from "./blocks/Seo.js";
import smitaHeadshot from "src/assets/smita_headshot.png";
import smitaSignature from "src/assets/signature.png";

/**
 * Ornamental section break. Three centered dots, used between numbered
 * sections to give the essay editorial rhythm.
 */
const OrnamentalBreak = ({ theme }) => (
  <Box
    aria-hidden
    sx={{
      display: "flex",
      justifyContent: "center",
      gap: 1.25,
      my: { xs: 6, md: 8 },
    }}
  >
    {[0, 1, 2].map((i) => (
      <Box
        key={i}
        sx={{
          width: 4,
          height: 4,
          borderRadius: "50%",
          bgcolor: theme.palette.text.tertiary,
        }}
      />
    ))}
  </Box>
);

/**
 * One numbered essay section with an editorial header (Roman-style numeral
 * kicker plus a bold display title) and body copy.
 */
const EssaySection = ({ numeral, title, children }) => {
  const theme = useTheme();
  return (
    <Box component="section">
      <Typography
        sx={{
          fontSize: { xs: 13, md: 14 },
          letterSpacing: "0.22em",
          textTransform: "uppercase",
          fontWeight: 700,
          color: theme.palette.success.main,
          mb: { xs: 2, md: 2.5 },
        }}
      >
        {numeral}
      </Typography>
      <Typography
        component="h2"
        sx={{
          fontSize: { xs: "1.875rem", md: "2.5rem" },
          fontWeight: 800,
          letterSpacing: "-0.025em",
          lineHeight: 1.1,
          color: theme.palette.text.primary,
          mb: { xs: 4, md: 5 },
          textWrap: "balance",
        }}
      >
        {title}
      </Typography>
      <Box
        sx={{
          "& p": {
            fontSize: { xs: "1.125rem", md: "1.1875rem" },
            lineHeight: 1.75,
            color: theme.palette.text.secondary,
            mt: 0,
            mb: 3,
            fontWeight: 400,
          },
          "& p:last-child": { mb: 0 },
          "& strong": {
            color: theme.palette.text.primary,
            fontWeight: 700,
          },
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

/**
 * Inline highlighter. Soft green marker background for phrases we want
 * the eye to catch mid-paragraph without breaking the reading flow.
 */
const Highlight = ({ children }) => (
  <Box
    component="mark"
    sx={{
      bgcolor: "rgba(32, 156, 51, 0.18)",
      color: "inherit",
      px: 0.6,
      py: 0.2,
      borderRadius: 0.5,
      boxDecorationBreak: "clone",
      WebkitBoxDecorationBreak: "clone",
    }}
  >
    {children}
  </Box>
);

/**
 * A pull quote for emphasizing a single line inside a section. Italic,
 * indented, thin green vertical rule on the left.
 */
const PullQuote = ({ children }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        my: { xs: 4, md: 5 },
        pl: { xs: 2.5, md: 3.5 },
        borderLeft: `3px solid ${theme.palette.success.main}`,
      }}
    >
      <Typography
        sx={{
          fontSize: { xs: "1.25rem", md: "1.5rem" },
          lineHeight: 1.4,
          color: theme.palette.text.primary,
          fontStyle: "italic",
          fontWeight: 400,
          textWrap: "balance",
        }}
      >
        {children}
      </Typography>
    </Box>
  );
};

const FounderNote = () => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        width: "100%",
        bgcolor: theme.palette.paper.primary,
        minHeight: "100vh",
              }}
    >
      <Seo
        title="A note from the founder · Maestro"
        description="Smita Saxena on why Maestro exists. The SF elementary classroom that started it, the centaur mindset behind human plus AI coaching, and why every teacher deserves what the best-funded schools have."
        type="article"
      />

      <MaestroNavbar />

      {/* Masthead */}
      <Box
        component="section"
        sx={{
          pt: { xs: 8, md: 12 },
          pb: { xs: 4, md: 6 },
          bgcolor: theme.palette.paper.primary,
        }}
      >
        <Container maxWidth="md">
          <Typography
            sx={{
    fontSize: { xs: 12, md: 13 },
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              fontWeight: 700,
              color: theme.palette.success.main,
              mb: 3,
              textAlign: "center",
            }}
          >
            A note from the founder
          </Typography>
          <Typography
            component="h1"
            sx={{
    fontSize: { xs: "2.5rem", md: "3.75rem" },
              fontWeight: 800,
              letterSpacing: "-0.035em",
              lineHeight: 1.05,
              color: theme.palette.text.primary,
              textAlign: "center",
              textWrap: "balance",
            }}
          >
            Why Maestro exists,{" "}
            <Box component="span" sx={{ color: theme.palette.success.main }}>
              in my own words
            </Box>
            .
          </Typography>
        </Container>
      </Box>

      {/* Opening pull-quote */}
      <Box
        component="section"
        sx={{
          py: { xs: 5, md: 8 },
          bgcolor: theme.palette.paper.primary,
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: "center", position: "relative" }}>
            <Typography
              aria-hidden
              sx={{
        fontSize: { xs: "5rem", md: "8rem" },
                lineHeight: 0.5,
                color: theme.palette.success.main,
                opacity: 0.35,
                userSelect: "none",
                mb: { xs: 2, md: 3 },
              }}
            >
              &ldquo;
            </Typography>
            <Typography
              sx={{
        fontSize: { xs: "1.5rem", md: "2rem" },
                fontWeight: 400,
                lineHeight: 1.4,
                color: theme.palette.text.primary,
                fontStyle: "italic",
                maxWidth: 760,
                mx: "auto",
                textWrap: "balance",
              }}
            >
              I'm a math major who lives in San Francisco. When I stepped away
              from my last company, I started volunteering at a local
              elementary school, teaching math. It was the first time I got a
              real view into what a teacher's day actually looks like.
            </Typography>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1.5,
                mt: 5,
              }}
            >
              <Box
                component="img"
                src={smitaHeadshot}
                alt="Smita Saxena"
                sx={{
                  width: 90,
                  height: 90,
                  borderRadius: "50%",
                  objectFit: "cover",
                }}
              />
              <Box sx={{ textAlign: "left" }}>
                <Typography
                  sx={{
                    fontSize: "1.5rem",
                    fontWeight: 700,
                    color: theme.palette.text.primary,
                  }}
                >
                  Smita Saxena
                </Typography>
                <Typography
                  sx={{
                fontSize: "1rem",
                    color: theme.palette.text.secondary,
                    lineHeight: 1.2,
                  }}
                >
                  Founder, Maestro
                </Typography>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="md">
        <OrnamentalBreak theme={theme} />
      </Container>

      {/* Essay body — narrow measure for readability */}
      <Box
        component="article"
        sx={{
          pb: { xs: 8, md: 12 },
          bgcolor: theme.palette.paper.primary,
        }}
      >
        <Container maxWidth="md">
          <EssaySection numeral="I." title="Thirty tools in the first thirty minutes.">
            <p>
              Within the first thirty minutes of that first classroom visit, I
              watched a teacher touch <strong>thirty different tools</strong>.
              It blew my mind.
            </p>
            <p>
              I come from tech, where everything is hyper-efficient. Teams are
              set up to succeed because there are dollars on the line, and the
              systems work for you, not against you.
            </p>
            <p>
              What I saw was a lot of duplicative work. Work that wasn't tied
              back to what actually impacted students, because that feedback
              loop didn't exist. In tech and product, we{" "}
              <strong>live and die off feedback loops</strong>. In the
              classroom, they'd been broken for years.
            </p>
            <p>
              And what I saw on the other side of it: the work teachers were
              doing manually wasn't because they wanted to. It was because the
              tools didn't exist to help. AI simply wasn't at the place where
              we could generate the materials at the scale a classroom needs.
              So teachers did it themselves. At 9pm. Every night.
            </p>
          </EssaySection>

          <OrnamentalBreak theme={theme} />

          <EssaySection numeral="II." title="The centaur mindset.">
            <p>
              When I first started building, I thought of Maestro as a
              tech-only tool. An app that would generate materials on demand.
              As I worked with teachers, I realized very quickly that the
              human piece wasn't optional. It's{" "}
              <strong>absolutely necessary</strong>.
            </p>
            <PullQuote>
              A human plus a machine beats either one alone. That's true in
              chess. It's true in classrooms.
            </PullQuote>
            <p>
              I call it the centaur mindset. It comes from chess. After IBM's Deep
              Blue beat the first grandmaster, there was a period where humans
              and machines played together. Human plus machine versus machine
              alone versus human alone. What they found was that the pairing
              almost always won.
            </p>
            <p>
              You can have every tool that generates materials. There's no
              shortage of that now. The question that actually matters is:{" "}
              <Highlight>
                what do you need, why do you need it, and how are you going to
                implement it. That's where the human coach comes in.
              </Highlight>
            </p>
          </EssaySection>

          <OrnamentalBreak theme={theme} />

          <EssaySection numeral="III." title="Teachers first. Always.">
            <p>
              We only work with Title I and charter schools. In those classrooms,{" "}
              <strong>
                40% of students are testing three or more grade levels below
              </strong>
              .
            </p>
            <p>
              So the challenge isn't just closing the gap between where the
              students are and where the curriculum says they should be. It's
              also closing the gap between the curriculum and the assessments.
              This is where AI is incredible: creating the supplemental
              materials that meet each student where they are.
            </p>
            <PullQuote>
              Happy teachers, happy students. That's the whole design.
            </PullQuote>
            <p>
              What surprised me most in Year 1 was how quickly the teachers
              embraced the program. I was hypersensitive coming in. Worried
              they'd see me as another tech person trying to solve problems
              for them. I wanted to build something inclusive.
            </p>
            <p>
              We started with five or six teachers six months ago. We're now
              over fifty in one area, and we don't have any salespeople. It's
              all grown by teachers recruiting other teachers. That was the
              validation. That was the{" "}
              <Highlight>"oh, this could work"</Highlight> moment.
            </p>
          </EssaySection>

          <OrnamentalBreak theme={theme} />

          <EssaySection numeral="IV." title="Teachers become coaches.">
            <p>
              About six to eight weeks into the first cohort, we had enough
              data to see that certain teachers were outperforming. Alexia
              Lewis at KIPP Polaris was one of them. We asked her, and a few
              others, if they wanted to take some of the four to eight hours a
              week Maestro was saving them and use it to coach other teachers.
            </p>
            <p>
              We watched their own performance carefully. The worry was that
              taking time away for coaching would hurt their students. It
              didn't.{" "}
              <strong>
                Every coach outperformed their cohort while taking on new
                teachers
              </strong>
              . Alexia is now one of our star coaches.
            </p>
            <PullQuote>
              Keep the best teachers in the classroom. Give them more income.
              Bring everyone up with them.
            </PullQuote>
            <p>
              That's the model. It's not about replacing teachers. It's about
              creating a career path that didn't exist for the ones who are
              already changing kids' lives.
            </p>
          </EssaySection>

          <OrnamentalBreak theme={theme} />

          <EssaySection numeral="V." title="Objective truth matters.">
            <p>
              We picked math and science partially because I'm a math major
              and I had opinions about what good instruction looks like. But
              also because these subjects have <strong>objective truth</strong>
              . When AI generates a worksheet, we can verify it's accurate.
            </p>
            <p>
              That matters. Hallucinations are a real problem and getting
              worse as models get more complex. We do a lot of internal
              checking to make sure everything we generate is right.
              Occasionally something slips through, and students catch it,
              which makes us happy that they've learned something.
            </p>
            <p>
              The point is: we have a framework for quality here that we don't
              yet have for ELA or history. When we do, we'll expand.
            </p>
          </EssaySection>

          <OrnamentalBreak theme={theme} />

          <EssaySection
            numeral="VI."
            title="Every school gets what the best-funded schools have."
          >
            <p>
              Dedicated instructional coaches. Differentiated materials.
              Real-time feedback loops. These used to be a{" "}
              <strong>private-school luxury</strong>. Maestro's job is to make
              them the baseline for every school.
            </p>
            <p>
              If you're a teacher, a principal, a district leader, or a funder
              who wants that world too, I'd love to hear from you.
            </p>
          </EssaySection>
        </Container>
      </Box>

      <ClosingCTA
        theme="dark"
        headline="Bring Maestro to"
        headlineAccent="your classroom"
        subtitle="We'd love to hear from you."
        ctaLabel="Join the waitlist →"
        sx={{
          mt: 1
        }}
      />

      <MaestroFooter variant="dark" />
    </Box>
  );
};

export default FounderNote;
